import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useDebounce } from './useDebounce'
import {
  searchStoreModels,
  listStoreModels,
  getStoreModelStores,
  listStoreModelPlans,
  getStoreModel,
  assignStoreModel,
  unassignStoreModel,
  bulkUpdateStoreGroups,
  createStoreModel,
  updateStoreModel,
  deleteStoreModel,
  overrideAllocations,
  resetAllocations,
  triggerRecalculation,
  applyGrouping,
  listModelGroups,
} from '@/api/storeModels'
import { queryKeys } from '@/lib/queryKeys'
import type {
  PaginatedStoreModelsResponse,
  PaginatedStoreModelMembersResponse,
  PaginatedStoreGroupsResponse,
  PaginatedPlansResponse,
  StoreModelDetailRead,
  BulkOperationResponse,
  ProcessingResponse,
  StoreModelCreate,
  StoreModelUpdate,
  ModelType,
  ModelStatus,
  AllocationOverrideRequest,
  AllocationOverrideResponse,
  ApplyGroupingRequest,
} from '@/types'
import type { AxiosError } from 'axios'

interface UseStoreModelSearchParams {
  query: string
  enabled?: boolean
}

export function useStoreModelSearch({
  query,
  enabled = true,
}: UseStoreModelSearchParams) {
  const debouncedQuery = useDebounce(query, 300)

  return useQuery<PaginatedStoreModelsResponse>({
    queryKey: queryKeys.storeModels.search(debouncedQuery),
    queryFn: () => searchStoreModels({ search: debouncedQuery }),
    enabled,
    staleTime: 1000 * 60,
  })
}

interface UseStoreModelStoresParams {
  modelId: string | null
  page: number
  pageSize: number
  search?: string
  sort?: string
  storeGroupId?: string
}

export function useStoreModelStores({
  modelId,
  page,
  pageSize,
  search = '',
  sort = '',
  storeGroupId = '',
}: UseStoreModelStoresParams) {
  const debouncedSearch = useDebounce(search, 300)

  const filters = {
    search: debouncedSearch,
    sort,
    storeGroupId,
    page,
    pageSize,
  }

  return useQuery<PaginatedStoreModelMembersResponse>({
    queryKey: queryKeys.storeModels.stores(modelId ?? '', filters),
    queryFn: () =>
      getStoreModelStores({
        modelId: modelId!,
        skip: (page - 1) * pageSize,
        limit: pageSize,
        search: debouncedSearch || undefined,
        sort: sort || undefined,
        store_group_id: storeGroupId || undefined,
      }),
    enabled: !!modelId,
    staleTime: 1000 * 60,
  })
}

interface UseStoreModelPlansParams {
  modelId: string | null
  page: number
  pageSize: number
}

export function useStoreModelPlans({
  modelId,
  page,
  pageSize,
}: UseStoreModelPlansParams) {
  return useQuery<PaginatedPlansResponse>({
    queryKey: queryKeys.storeModels.plans(modelId ?? '', { page, pageSize }),
    queryFn: () =>
      listStoreModelPlans({
        modelId: modelId!,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      }),
    enabled: !!modelId,
    staleTime: 1000 * 60,
  })
}

interface UseStoreModelsListParams {
  search: string
  modelType: ModelType | 'all'
  status: ModelStatus | 'all'
  sort: string
  page: number
  pageSize: number
}

export function useStoreModelsList({
  search,
  modelType,
  status,
  sort,
  page,
  pageSize,
}: UseStoreModelsListParams) {
  const debouncedSearch = useDebounce(search, 300)

  const filters = {
    search: debouncedSearch,
    modelType: modelType === 'all' ? undefined : modelType,
    status: status === 'all' ? undefined : status,
    sort,
    page,
    pageSize,
  }

  return useQuery<PaginatedStoreModelsResponse>({
    queryKey: queryKeys.storeModels.list(filters),
    queryFn: () =>
      listStoreModels({
        search: debouncedSearch || undefined,
        model_type: modelType === 'all' ? undefined : modelType,
        status: status === 'all' ? undefined : status,
        sort,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      }),
  })
}

export function useStoreModelDetail(modelId: string | undefined) {
  return useQuery<StoreModelDetailRead>({
    queryKey: queryKeys.storeModels.detail(modelId ?? ''),
    queryFn: () => getStoreModel(modelId!),
    enabled: !!modelId,
    staleTime: 1000 * 60,
  })
}

export function useCreateStoreModel() {
  const queryClient = useQueryClient()
  return useMutation<ProcessingResponse, AxiosError, StoreModelCreate>({
    mutationFn: createStoreModel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.storeModels.all })
    },
  })
}

export function useUpdateStoreModel() {
  const queryClient = useQueryClient()
  return useMutation<
    ProcessingResponse,
    AxiosError,
    { modelId: string; payload: StoreModelUpdate }
  >({
    mutationFn: ({ modelId, payload }) => updateStoreModel(modelId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.storeModels.all })
    },
  })
}

export function useDeleteStoreModel() {
  const queryClient = useQueryClient()
  return useMutation<void, AxiosError, string>({
    mutationFn: deleteStoreModel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.storeModels.all })
    },
  })
}

export function useOverrideAllocations(modelId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    AllocationOverrideResponse,
    AxiosError,
    AllocationOverrideRequest
  >({
    mutationFn: payload => overrideAllocations(modelId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeModels.stores(modelId, {}),
        exact: false,
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeModels.detail(modelId),
      })
    },
  })
}

export function useResetAllocations(modelId: string) {
  const queryClient = useQueryClient()
  return useMutation<AllocationOverrideResponse, AxiosError, void>({
    mutationFn: () => resetAllocations(modelId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeModels.stores(modelId, {}),
        exact: false,
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeModels.detail(modelId),
      })
    },
  })
}

export function useTriggerRecalculation(modelId: string) {
  const queryClient = useQueryClient()
  return useMutation<ProcessingResponse, AxiosError, void>({
    mutationFn: () => triggerRecalculation(modelId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeModels.detail(modelId),
      })
    },
  })
}

export function useApplyGrouping(modelId: string) {
  const queryClient = useQueryClient()
  return useMutation<ProcessingResponse, AxiosError, ApplyGroupingRequest>({
    mutationFn: payload => applyGrouping(modelId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeModels.detail(modelId),
      })
      // Groups invalidation is handled by onGroupingApplied after processing
      // completes — invalidating here would refetch before groups are created.
    },
  })
}

export function useModelGroups(modelId: string | undefined, search: string) {
  const debouncedSearch = useDebounce(search, 300)

  return useQuery<PaginatedStoreGroupsResponse>({
    queryKey: [...queryKeys.storeModels.groups(modelId ?? ''), debouncedSearch],
    queryFn: () =>
      listModelGroups({
        modelId: modelId!,
        search: debouncedSearch || undefined,
        limit: 100,
      }),
    enabled: !!modelId,
    staleTime: 1000 * 60,
  })
}

export function useModelGroupsInfinite(
  modelId: string | undefined,
  search: string
) {
  const debouncedSearch = useDebounce(search, 300)

  return useInfiniteQuery<PaginatedStoreGroupsResponse>({
    queryKey: [
      ...queryKeys.storeModels.groups(modelId ?? ''),
      'infinite',
      debouncedSearch,
    ],
    queryFn: ({ pageParam = 0 }) =>
      listModelGroups({
        modelId: modelId!,
        search: debouncedSearch || undefined,
        skip: pageParam as number,
        limit: 50,
      }),
    getNextPageParam: lastPage =>
      lastPage.pagination.has_more
        ? lastPage.pagination.skip + lastPage.pagination.limit
        : undefined,
    initialPageParam: 0,
    enabled: !!modelId,
    staleTime: 1000 * 60,
  })
}

export function useAssignStoreModel(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineIds,
      storeModelId,
    }: {
      lineIds: string[]
      storeModelId: string
    }) =>
      assignStoreModel(planId, versionId, {
        line_ids: lineIds,
        store_model_id: storeModelId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(planId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.distributionCenters.all,
        exact: false,
      })
      if (versionId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
        })
      }
    },
  })
}

export function useUnassignStoreModel(planId: string, versionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ lineIds }: { lineIds: string[] }) =>
      unassignStoreModel(planId, versionId, { line_ids: lineIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(planId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.distributionCenters.all,
        exact: false,
      })
      if (versionId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
        })
      }
    },
  })
}

export function useUpdateStoreGroups(planId: string, versionId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    BulkOperationResponse,
    AxiosError,
    { lineIds: string[]; storeGroupIds: string[] }
  >({
    mutationFn: ({ lineIds, storeGroupIds }) =>
      bulkUpdateStoreGroups(planId, versionId, {
        line_ids: lineIds,
        store_group_ids: storeGroupIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(planId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.distributionCenters.all,
        exact: false,
      })
      if (versionId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
        })
      }
    },
  })
}
